import type { PostHogEventLike } from "./posthog-privacy";

export type BrowserNoiseSource =
	| "wallet_injection"
	| "darkreader"
	| "script_error_without_frames"
	| "object_not_found_matching_id"
	| "resize_observer_loop";

function record(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function exceptionSource(exception: unknown): BrowserNoiseSource | undefined {
	if (!record(exception) || typeof exception.value !== "string") return undefined;
	const message = exception.value.trim();
	const stack = exception.stacktrace;
	if (stack !== undefined && (!record(stack) || (stack.frames !== undefined && !Array.isArray(stack.frames))))
		return undefined;
	const frames: unknown[] = record(stack) && Array.isArray(stack.frames) ? stack.frames : [];
	const sources = frames.flatMap((frame) =>
		record(frame)
			? [frame.filename, frame.abs_path].filter((source): source is string => typeof source === "string")
			: [],
	);
	// A known browser-noise phrase inside our application stack still needs investigation.
	if (sources.some((source) => /(?:^|\/)_(?:next)\/|webpack(?:-internal)?:\/\/|(?:^|\/)src\//i.test(source)))
		return undefined;
	if (
		/\bwindow\.ethereum\b|\b(?:redefine property|read only property|readonly property).*?\bethereum\b/i.test(
			message,
		)
	)
		return "wallet_injection";
	if (/\b(?:__darkreader__[\w$]*|darkreader)\b/i.test(message)) return "darkreader";
	if (message === "Script error." && frames.length === 0) return "script_error_without_frames";
	if (
		/^(?:Non-Error promise rejection captured with value:\s*)?Object Not Found Matching Id:\s*\d+,\s*MethodName:\s*[\w.]+,\s*ParamCount:\s*\d+\s*$/i.test(
			message,
		)
	)
		return "object_not_found_matching_id";
	if (/^ResizeObserver loop (?:limit exceeded|completed with undelivered notifications)\.?$/.test(message))
		return "resize_observer_loop";
	return undefined;
}

/** Tag only homogeneous known-noise exceptions; mixed/unknown error chains stay untouched. */
export function browserExceptionNoiseSource(event: PostHogEventLike): BrowserNoiseSource | undefined {
	if (event.event !== "$exception") return undefined;
	const list = event.properties?.$exception_list;
	if (!Array.isArray(list) || list.length === 0) return undefined;
	const sources = list.map(exceptionSource);
	const source = sources[0];
	return source && sources.every((value) => value === source) ? source : undefined;
}

const FOOTER_PATH = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:(?:eu|us)\/)?(?:privacy|impressum|unsubscribe)\/?$/i;
const SCANNER_SIGNAL = "mail_link_scanner_pattern";
// These events can be emitted automatically; business/funnel actions are never marked here.
const PASSIVE_EVENTS = new Set(["$exception", "$pageview", "$pageleave", "$web_vitals", "$autocapture"]);
const SHOP_EVENTS =
	/^(?:checkout(?:_|$)|add_to_cart$|product_added_to_cart$|order_completed$|payment(?:_|$)|vorkasse_order_placed$)/;

/** Per-document evidence only: never persisted to an identified person or later visits. */
export function createBrowserNoiseEnricher() {
	let scannerPatternSeen = false;
	let interacted = false;
	return {
		noteInteraction() {
			interacted = true;
			scannerPatternSeen = false;
		},
		enrich<T extends PostHogEventLike>(event: T, context: { userAgent: string; pathname: string }): T {
			try {
				const noise = browserExceptionNoiseSource(event);
				if (SHOP_EVENTS.test(event.event ?? "")) {
					interacted = true;
					scannerPatternSeen = false;
				}
				const footerChromeWindows =
					FOOTER_PATH.test(context.pathname) &&
					/Windows NT/.test(context.userAgent) &&
					/Chrome\/\d+/.test(context.userAgent) &&
					!/Edg\/|OPR\/|Mobile|Android/.test(context.userAgent);
				if (!interacted && footerChromeWindows && noise === "object_not_found_matching_id")
					scannerPatternSeen = true;
				const scanner =
					!interacted &&
					scannerPatternSeen &&
					footerChromeWindows &&
					PASSIVE_EVENTS.has(event.event ?? "") &&
					(event.event !== "$exception" || noise !== undefined);
				if (!noise && !scanner) return event;
				const signals =
					typeof event.properties?.bot_signals === "string"
						? event.properties.bot_signals.split(",").filter(Boolean)
						: [];
				return {
					...event,
					properties: {
						...event.properties,
						...(noise ? { noise_source: noise } : {}),
						...(scanner
							? { suspected_bot: true, bot_signals: [...new Set([...signals, SCANNER_SIGNAL])].join(",") }
							: {}),
					},
				};
			} catch {
				return event;
			}
		},
	};
}
